// ====================================================
// VidyaTrack — Reports & Analytics  (real Firebase data only)
// ====================================================

import React, { useState, useEffect } from "react";
import { useData } from "../contexts/DataContext";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import { format, subMonths } from "date-fns";

const toDate = v => {
  if (!v) return new Date();
  if (typeof v?.toDate === "function") return v.toDate();
  return new Date(v);
};

export default function ReportsPage() {
  const { getStudents, getTeachers, getClasses, getHomework, getAttendance, getSubmissions } = useData();
  const navigate = useNavigate();

  const [students,   setStudents]   = useState([]);
  const [teachers,   setTeachers]   = useState([]);
  const [classes,    setClasses]    = useState([]);
  const [homework,   setHomework]   = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [submissions,setSubmissions]= useState([]);
  const [loading,    setLoading]    = useState(true);
  const [tab,        setTab]        = useState("overview"); // overview | attendance | homework | students

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [st, tc, cl, hw, att, sub] = await Promise.all([
        getStudents(), getTeachers(), getClasses(),
        getHomework(), getAttendance({}), getSubmissions({}),
      ]);
      setStudents(st); setTeachers(tc); setClasses(cl);
      setHomework(hw); setAttendance(att); setSubmissions(sub);
    } catch { /* handled silently */ }
    finally { setLoading(false); }
  };

  // ── Derived metrics ──────────────────────────────────────
  const totalPresent  = attendance.filter(a => a.status === "present").length;
  const totalAbsent   = attendance.filter(a => a.status === "absent").length;
  const totalLate     = attendance.filter(a => a.status === "late").length;
  const totalAtt      = attendance.length;
  const overallAttPct = totalAtt > 0 ? Math.round((totalPresent / totalAtt) * 100) : 0;

  const attPie = [
    { name: "Present", value: totalPresent, color: "#10b981" },
    { name: "Absent",  value: totalAbsent,  color: "#ef4444" },
    { name: "Late",    value: totalLate,    color: "#f59e0b" },
  ].filter(d => d.value > 0);

  // Homework submission rate per assignment
  const hwBar = homework
    .filter(h => h.totalStudents > 0)
    .map(h => ({
      name: (h.title || "").slice(0, 12) + ((h.title || "").length > 12 ? "…" : ""),
      subject: h.subject || "",
      pct: Math.round(((h.submissionsCount || 0) / h.totalStudents) * 100),
      submitted: h.submissionsCount || 0,
      total: h.totalStudents,
    }))
    .slice(0, 8);

  // Homework by subject — group and average
  const subjectMap = {};
  homework.forEach(h => {
    if (!h.subject) return;
    if (!subjectMap[h.subject]) subjectMap[h.subject] = { total: 0, submitted: 0, count: 0 };
    subjectMap[h.subject].total     += h.totalStudents || 0;
    subjectMap[h.subject].submitted += h.submissionsCount || 0;
    subjectMap[h.subject].count     += 1;
  });
  const subjectBar = Object.entries(subjectMap).map(([subject, d]) => ({
    subject: subject.slice(0, 8),
    completion: d.total > 0 ? Math.round((d.submitted / d.total) * 100) : 0,
  })).sort((a, b) => b.completion - a.completion);

  // Attendance by class
  const classAttMap = {};
  attendance.forEach(a => {
    if (!a.classId) return;
    if (!classAttMap[a.classId]) classAttMap[a.classId] = { present: 0, total: 0, name: a.classId };
    classAttMap[a.classId].total++;
    if (a.status === "present") classAttMap[a.classId].present++;
  });
  const classAttBar = Object.values(classAttMap).map(d => ({
    class: (classes.find(c => c.id === d.name)?.name || d.name).replace("Class ", "Cls "),
    pct: d.total > 0 ? Math.round((d.present / d.total) * 100) : 0,
  })).slice(0, 8);

  // Monthly attendance trend — last 6 months
  const monthlyTrend = Array.from({ length: 6 }, (_, i) => {
    const month = subMonths(new Date(), 5 - i);
    const monthStr = format(month, "yyyy-MM");
    const monthAtt = attendance.filter(a => {
      const d = typeof a.date === "string" ? a.date : format(toDate(a.date), "yyyy-MM-dd");
      return d.startsWith(monthStr);
    });
    const p = monthAtt.filter(a => a.status === "present").length;
    const t = monthAtt.length;
    return {
      month: format(month, "MMM"),
      rate: t > 0 ? Math.round((p / t) * 100) : 0,
      total: t,
    };
  });

  // Students at risk (< 75% attendance) — only if we have per-student data
  const studentAttMap = {};
  attendance.forEach(a => {
    if (!a.studentId) return;
    if (!studentAttMap[a.studentId]) studentAttMap[a.studentId] = { present: 0, total: 0, studentId: a.studentId };
    studentAttMap[a.studentId].total++;
    if (a.status === "present") studentAttMap[a.studentId].present++;
  });
  const atRisk = Object.values(studentAttMap)
    .filter(d => d.total >= 5 && (d.present / d.total) < 0.75)
    .map(d => ({
      ...d,
      student: students.find(s => s.id === d.studentId),
      pct: Math.round((d.present / d.total) * 100),
    }))
    .filter(d => d.student)
    .slice(0, 10);

  const TABS = [
    { id: "overview",   label: "Overview"   },
    { id: "attendance", label: "Attendance" },
    { id: "homework",   label: "Homework"   },
    { id: "students",   label: "Students"   },
  ];

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="animate-fade">
      <div className="page-head">
        <div>
          <h1 className="page-title">📊 Reports & Analytics</h1>
          <p className="page-subtitle">Live data from your school — {format(new Date(), "MMMM d, yyyy")}</p>
        </div>
        <button className="btn btn-secondary" onClick={() => window.print()}>🖨 Print Report</button>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap", overflowX:"auto" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding:"7px 18px", borderRadius:"var(--r8)",
            border:`1.5px solid ${tab===t.id?"var(--blue-700)":"var(--border)"}`,
            background: tab===t.id ? "var(--blue-700)" : "#fff",
            color: tab===t.id ? "#fff" : "var(--gray-500)",
            fontWeight:600, fontSize:".84rem", cursor:"pointer",
            fontFamily:"inherit", transition:"var(--t)",
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === "overview" && (
        <div>
          {/* KPI row */}
          <div className="g g4" style={{ marginBottom:18 }}>
            {[
              { label:"Total Students",    value:students.length,    icon:"👤", c:"var(--blue-700)", bg:"var(--blue-100)" },
              { label:"Teachers",          value:teachers.length,    icon:"👩‍🏫", c:"var(--green-600)",   bg:"var(--green-50)" },
              { label:"Overall Attendance",value:`${overallAttPct}%`,icon:"✅", c:overallAttPct>=75?"var(--green-600)":"var(--red-600)", bg:overallAttPct>=75?"var(--green-50)":"var(--red-50)" },
              { label:"Assignments",       value:homework.length,    icon:"📚", c:"var(--amber-600)",  bg:"var(--amber-50)" },
            ].map(k => (
              <div key={k.label} className="card" style={{ padding:"16px 18px", display:"flex", alignItems:"center", gap:14 }}>
                <div style={{ width:46, height:46, borderRadius:"var(--r8)", background:k.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.3rem" }}>{k.icon}</div>
                <div>
                  <div style={{ fontSize:"1.5rem", fontWeight:800, color:k.c, fontFamily:"'Poppins',sans-serif", lineHeight:1 }}>{k.value}</div>
                  <div style={{ fontSize:".75rem", color:"var(--gray-500)", marginTop:3 }}>{k.label}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="g g2" style={{ marginBottom:16 }}>
            {/* Monthly Trend */}
            <div className="card">
              <div className="card-header"><span className="card-title">📈 Monthly Attendance Trend</span></div>
              <div className="card-body">
                {monthlyTrend.every(m => m.total === 0) ? (
                  <div className="empty-state" style={{ padding:"24px 0" }}>
                    <div className="empty-icon">📅</div>
                    <div className="empty-title">No attendance records yet</div>
                    <div className="empty-desc">Start marking attendance to see trends here.</div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={monthlyTrend} margin={{ top:5, right:10, bottom:5, left:-20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="month" tick={{ fontSize:11 }} />
                      <YAxis domain={[0,100]} tick={{ fontSize:11 }} />
                      <Tooltip formatter={v => [`${v}%`, "Attendance"]} contentStyle={{ borderRadius:8, fontSize:".82rem" }} />
                      <Line type="monotone" dataKey="rate" stroke="#054bb4" strokeWidth={2.5} dot={{ r:4 }} name="Attendance %" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Attendance Breakdown */}
            <div className="card">
              <div className="card-header"><span className="card-title">📊 Attendance Breakdown</span></div>
              <div className="card-body">
                {totalAtt === 0 ? (
                  <div className="empty-state" style={{ padding:"24px 0" }}>
                    <div className="empty-icon">📊</div>
                    <div className="empty-title">No records yet</div>
                  </div>
                ) : (
                  <div style={{ display:"flex", alignItems:"center", gap:20 }}>
                    <ResponsiveContainer width={140} height={140}>
                      <PieChart>
                        <Pie data={attPie} dataKey="value" cx="50%" cy="50%" innerRadius={38} outerRadius={62}>
                          {attPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius:8, fontSize:".8rem" }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ flex:1 }}>
                      {attPie.map(d => (
                        <div key={d.name} style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ width:9, height:9, borderRadius:"50%", background:d.color }} />
                            <span style={{ fontSize:".84rem" }}>{d.name}</span>
                          </div>
                          <div>
                            <span style={{ fontWeight:700, color:d.color, fontSize:".84rem" }}>{d.value}</span>
                            <span style={{ fontSize:".74rem", color:"var(--gray-500)", marginLeft:4 }}>
                              ({totalAtt > 0 ? Math.round((d.value/totalAtt)*100) : 0}%)
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Homework by subject */}
          {subjectBar.length > 0 && (
            <div className="card" style={{ marginBottom:16 }}>
              <div className="card-header"><span className="card-title">📚 Homework Completion by Subject</span></div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={subjectBar} margin={{ top:5, right:10, bottom:5, left:-20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="subject" tick={{ fontSize:11 }} />
                    <YAxis domain={[0,100]} tick={{ fontSize:11 }} />
                    <Tooltip formatter={v => [`${v}%`, "Completion"]} contentStyle={{ borderRadius:8, fontSize:".82rem" }} />
                    <Bar dataKey="completion" fill="#054bb4" radius={[4,4,0,0]}>
                      {subjectBar.map((e, i) => <Cell key={i} fill={e.completion>=80?"#10b981":e.completion>=50?"#f59e0b":"#ef4444"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ATTENDANCE ── */}
      {tab === "attendance" && (
        <div>
          {totalAtt === 0 ? (
            <div className="card"><div className="empty-state">
              <div className="empty-icon">✅</div>
              <div className="empty-title">No attendance records yet</div>
              <div className="empty-desc">Teachers need to mark attendance first.</div>
            </div></div>
          ) : (
            <>
              <div className="g g3" style={{ marginBottom:16 }}>
                {[
                  { label:"Total Records", value:totalAtt,        c:"var(--blue-700)", bg:"var(--blue-100)" },
                  { label:"Present",       value:totalPresent,    c:"var(--green-600)",   bg:"var(--green-50)" },
                  { label:"Absent",        value:totalAbsent,     c:"var(--red-600)",     bg:"var(--red-50)" },
                  { label:"Late",          value:totalLate,       c:"var(--amber-600)",  bg:"var(--amber-50)" },
                  { label:"Overall Rate",  value:`${overallAttPct}%`, c:overallAttPct>=75?"var(--green-600)":"var(--red-600)", bg:overallAttPct>=75?"var(--green-50)":"var(--red-50)" },
                  { label:"At Risk (<75%)",value:atRisk.length,   c:"var(--red-600)",     bg:"var(--red-50)" },
                ].map(k => (
                  <div key={k.label} className="card" style={{ padding:"14px 16px", textAlign:"center" }}>
                    <div style={{ fontSize:"1.6rem", fontWeight:800, color:k.c, fontFamily:"'Poppins',sans-serif", lineHeight:1 }}>{k.value}</div>
                    <div style={{ fontSize:".74rem", color:"var(--gray-500)", marginTop:4 }}>{k.label}</div>
                  </div>
                ))}
              </div>

              {classAttBar.length > 0 && (
                <div className="card" style={{ marginBottom:16 }}>
                  <div className="card-header"><span className="card-title">Attendance by Class</span></div>
                  <div className="card-body">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={classAttBar} margin={{ top:5, right:10, bottom:30, left:-20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                        <XAxis dataKey="class" tick={{ fontSize:11 }} angle={-20} textAnchor="end" />
                        <YAxis domain={[0,100]} tick={{ fontSize:11 }} />
                        <Tooltip formatter={v => [`${v}%`, "Attendance"]} contentStyle={{ borderRadius:8, fontSize:".82rem" }} />
                        <Bar dataKey="pct" fill="#054bb4" radius={[4,4,0,0]}>
                          {classAttBar.map((e, i) => <Cell key={i} fill={e.pct>=75?"#10b981":e.pct>=60?"#f59e0b":"#ef4444"} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {atRisk.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <span className="card-title">⚠️ Students at Risk (Below 75%)</span>
                    <span className="badge badge-danger" style={{ fontSize:".7rem" }}>{atRisk.length} students</span>
                  </div>
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead><tr><th>Student</th><th>Class</th><th>Present</th><th>Total</th><th>Rate</th></tr></thead>
                      <tbody>
                        {atRisk.map(d => (
                          <tr key={d.studentId}>
                            <td style={{ fontWeight:600 }}>{d.student?.name}</td>
                            <td style={{ fontSize:".82rem" }}>{d.student?.className || d.student?.classId}</td>
                            <td>{d.present}</td>
                            <td>{d.total}</td>
                            <td>
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <div className="progress" style={{ width:70 }}>
                                  <div className="progress-bar red" style={{ width:`${d.pct}%` }} />
                                </div>
                                <span style={{ fontWeight:700, color:"var(--red-600)", fontSize:".82rem" }}>{d.pct}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── HOMEWORK ── */}
      {tab === "homework" && (
        <div>
          {homework.length === 0 ? (
            <div className="card"><div className="empty-state">
              <div className="empty-icon">📚</div>
              <div className="empty-title">No homework assigned yet</div>
              <div className="empty-desc">Homework reports will appear here once teachers start assigning.</div>
            </div></div>
          ) : (
            <>
              <div className="g g4" style={{ marginBottom:16 }}>
                {[
                  { label:"Total Assignments",   value:homework.length,     c:"var(--blue-700)", bg:"var(--blue-100)" },
                  { label:"Total Submissions",   value:submissions.length,  c:"var(--green-600)",   bg:"var(--green-50)" },
                  { label:"Graded",              value:submissions.filter(s=>s.status==="graded").length, c:"#7c3aed", bg:"#f5f3ff" },
                  { label:"Pending Review",      value:submissions.filter(s=>s.status==="submitted").length, c:"var(--amber-600)", bg:"var(--amber-50)" },
                ].map(k => (
                  <div key={k.label} className="card" style={{ padding:"14px 16px", textAlign:"center" }}>
                    <div style={{ fontSize:"1.5rem", fontWeight:800, color:k.c, fontFamily:"'Poppins',sans-serif", lineHeight:1 }}>{k.value}</div>
                    <div style={{ fontSize:".74rem", color:"var(--gray-500)", marginTop:4 }}>{k.label}</div>
                  </div>
                ))}
              </div>

              {hwBar.length > 0 && (
                <div className="card" style={{ marginBottom:16 }}>
                  <div className="card-header"><span className="card-title">Submission Rate per Assignment</span></div>
                  <div className="card-body">
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={hwBar} margin={{ top:5, right:10, bottom:50, left:-20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                        <XAxis dataKey="name" tick={{ fontSize:10 }} angle={-30} textAnchor="end" />
                        <YAxis domain={[0,100]} tick={{ fontSize:11 }} />
                        <Tooltip contentStyle={{ borderRadius:8, fontSize:".82rem" }} formatter={v => [`${v}%`, "Submitted"]} />
                        <Bar dataKey="pct" fill="#054bb4" radius={[4,4,0,0]}>
                          {hwBar.map((e, i) => <Cell key={i} fill={e.pct>=70?"#10b981":e.pct>=40?"#f59e0b":"#ef4444"} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              <div className="card">
                <div className="card-header"><span className="card-title">All Assignments</span></div>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead><tr><th>Title</th><th>Subject</th><th>Class</th><th>Submitted</th><th>Total</th><th>Rate</th></tr></thead>
                    <tbody>
                      {homework.map(h => {
                        const pct = h.totalStudents > 0 ? Math.round(((h.submissionsCount||0)/h.totalStudents)*100) : 0;
                        return (
                          <tr key={h.id}>
                            <td style={{ fontWeight:600, fontSize:".88rem" }}>{h.title}</td>
                            <td><span className="badge badge-blue" style={{ fontSize:".68rem" }}>{h.subject}</span></td>
                            <td style={{ fontSize:".82rem" }}>{h.className}</td>
                            <td>{h.submissionsCount || 0}</td>
                            <td>{h.totalStudents || 0}</td>
                            <td>
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <div className="progress" style={{ width:60 }}>
                                  <div className={`progress-bar ${pct>=70?"green":pct>=40?"amber":"red"}`} style={{ width:`${pct}%` }} />
                                </div>
                                <span style={{ fontWeight:700, fontSize:".82rem" }}>{pct}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── STUDENTS ── */}
      {tab === "students" && (
        <div>
          {students.length === 0 ? (
            <div className="card"><div className="empty-state">
              <div className="empty-icon">👤</div>
              <div className="empty-title">No students enrolled yet</div>
              <div className="empty-desc">Add students from the Students page.</div>
            </div></div>
          ) : (
            <>
              <div className="g g3" style={{ marginBottom:16 }}>
                {[
                  { label:"Total Students",  value:students.length,                                              c:"var(--blue-700)", bg:"var(--blue-100)" },
                  { label:"Classes",         value:classes.length,                                              c:"#7c3aed",        bg:"#f5f3ff" },
                  { label:"At Risk",         value:atRisk.length,                                               c:"var(--red-600)",     bg:"var(--red-50)" },
                ].map(k => (
                  <div key={k.label} className="card" style={{ padding:"14px 16px", textAlign:"center" }}>
                    <div style={{ fontSize:"1.5rem", fontWeight:800, color:k.c, fontFamily:"'Poppins',sans-serif", lineHeight:1 }}>{k.value}</div>
                    <div style={{ fontSize:".74rem", color:"var(--gray-500)", marginTop:4 }}>{k.label}</div>
                  </div>
                ))}
              </div>

              <div className="card">
                <div className="card-header">
                  <span className="card-title">All Students</span>
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate("/admin/students")}>Manage</button>
                </div>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead><tr><th>#</th><th>Student</th><th>Roll No.</th><th>Class</th><th>Email</th></tr></thead>
                    <tbody>
                      {students.map((s, i) => (
                        <tr key={s.id}>
                          <td style={{ color:"var(--gray-400)", fontWeight:600 }}>{i+1}</td>
                          <td>
                            <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                              <div className="avatar av-sm" style={{ background:`hsl(${i*55},60%,88%)`, color:`hsl(${i*55},60%,30%)`, fontSize:".62rem" }}>
                                {(s.name||"?").split(" ").map(n=>n[0]).join("").slice(0,2)}
                              </div>
                              <span style={{ fontWeight:500, fontSize:".88rem" }}>{s.name}</span>
                            </div>
                          </td>
                          <td style={{ fontSize:".82rem", color:"var(--gray-500)" }}>{s.rollNumber || "—"}</td>
                          <td style={{ fontSize:".82rem" }}>{s.className || s.classId || "—"}</td>
                          <td style={{ fontSize:".8rem", color:"var(--gray-500)" }}>{s.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
